<template>
  <div class="share-waiver-modal">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        class="share-waiver-modal !w-[40%] !max-w-[40%] !top-[10vh] !translate-y-0"
        :show-close-button="false"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <!-- Header -->
        <SDialogHeader>
          <div class="modal-header d-flex fl-row fl-a-c fl-s-b">
            <div class="d-flex fl-row fl-a-c">
              <SvgIcon
                :iconContent="waiverIcon"
                iconAlt="waiver"
                iconStyle="width: 20px; height: 20px; margin-right: 8px;"
              />
              <SDialogTitle
                class="modal-title"
                data-cy="share-waiver-modal-title"
              >
                {{ $t("waiver.waivers-not-signed") }}
              </SDialogTitle>
            </div>
            <div>
              <span
                class="close-button"
                @click="close"
                data-cy="share-waiver-modal-close"
              >
                {{ $t("button.close") }}
              </span>
            </div>
          </div>
        </SDialogHeader>

        <!-- Body -->
        <div class="share-waiver-body d-flex fl-col fl-a-c">
          <!-- Loading state -->
          <div v-if="isLoading" class="loading-state">
            <el-icon class="is-loading"><Loading /></el-icon>
          </div>

          <!-- QR Code — only shown when url is ready -->
          <template v-else-if="waiverUrl">
            <div class="qr-wrapper" data-cy="share-waiver-qr-code">
              <qrcode-vue
                :value="waiverUrl"
                :size="200"
                level="H"
                render-as="svg"
              />
            </div>
            <p class="share-description" data-cy="share-waiver-description">
              {{ $t("waiver.customer-didnt-sign") }}
            </p>
            <p class="share-sub-description" data-cy="share-waiver-instruction">
              {{ $t("waiver.share-qr-instruction") }}
            </p>
          </template>

          <!-- Error state -->
          <div v-else class="error-state" data-cy="share-waiver-error">
            <p>{{ $t("waiver.no-active-template") }}</p>
          </div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import { SvgIcon } from "@/components/index";
import QrcodeVue from "qrcode.vue";
import { Loading } from "@element-plus/icons-vue";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { ModalStatus } from "@/constants/modal";
import BookingModule from "@/store/modules/booking";
import { getActiveWaiverTemplateVersionId } from "@/api/waivers/waivers";
import NotificationService from "@/services/notification.service";
import waiverIcon from "@/assets/images/svg/link.svg";
import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "ShareWaiverModal",
  components: {
    SvgIcon,
    QrcodeVue,
    Loading,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
  },
  setup() {
    const dialogVisible = dialogVisibleForStatus(ModalStatus.ShareWaiver);
    const waiverUrl = ref<string | null>(null);
    const isLoading = ref(false);

    const baseUrl =
      import.meta.env.VITE_WAIVER_URL ?? "https://waiver.smeetz.com/en";

    // Fetch active template version id when modal opens
    watch(dialogVisible, async (val) => {
      if (!val) return;
      isLoading.value = true;
      waiverUrl.value = null;
      try {
        const templateVersionId = await getActiveWaiverTemplateVersionId();
        if (templateVersionId) {
          waiverUrl.value = `${baseUrl}?tempid=${templateVersionId}`;
        }
      } catch (e) {
        NotificationService.showGeneralResponseError();
      } finally {
        isLoading.value = false;
      }
    });

    function close() {
      dialogVisible.value = false;
    }

    return {
      dialogVisible,
      waiverUrl,
      isLoading,
      close,
      waiverIcon,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.share-waiver-modal {
  .modal-header {
    padding: 4px 0;

    .modal-title {
      font-size: 16px;
      font-weight: 700;
      color: $color-text-primary;
    }

    .close-button {
      font-size: 14px;
      color: $color-dark-cyan;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .share-waiver-body {
    padding: 16px 0;
    gap: 16px;

    .qr-wrapper {
      padding: 16px;
      background: white;
      border-radius: 8px;
      display: inline-flex;
    }

    .share-description {
      font-size: 15px;
      font-weight: 700;
      text-align: center;
      color: $color-text-primary;
    }

    .share-sub-description {
      font-size: 13px;
      font-weight: 400;
      text-align: center;
      color: $color-light-grey2;
      max-width: 320px;
    }
  }

  .loading-state {
    text-align: center;
    padding: 24px 0;
    font-size: 24px;
  }

  .error-state {
    text-align: center;
    padding: 24px 0;
    color: $color-danger-red;
    font-size: 13px;
  }
}
</style>
